/**
 * Members area — placeholder folder for future gated features. Wraps the
 * standard `.com__*` chrome so items appear as icons the user can preview.
 * Nothing behind these icons yet; each is a stub that toasts a "coming soon"
 * message. Server-side gating is enforced by any future endpoints via
 * `require_user()` / `require_admin()` in the PHP layer.
 */

import React, { useState } from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { useAuth } from 'auth/AuthContext';

import go from 'assets/windowsIcons/290.png';
import back from 'assets/windowsIcons/back.png';
import forward from 'assets/windowsIcons/forward.png';
import up from 'assets/windowsIcons/up.png';
import search from 'assets/windowsIcons/299(32x32).png';
import folderOpen from 'assets/windowsIcons/337(32x32).png';
import menu from 'assets/windowsIcons/358(32x32).png';
import dropdown from 'assets/windowsIcons/dropdown.png';
import pullup from 'assets/windowsIcons/pullup.png';
import windows from 'assets/windowsIcons/windows.png';
import folderIcon from 'assets/windowsIcons/folder.png';

// 32×32 icons for the placeholder items.
import mediaPlayerIcon from 'assets/windowsIcons/846(32x32).png';
import chatIcon from 'assets/windowsIcons/msn.png';
import pinballIcon from 'assets/windowsIcons/pinball.png';
import shareIcon from 'assets/windowsIcons/309(32x32).png';

const dropDownData = {
  File: [
    { type: 'item', text: 'Open', hotkey: 'Enter' },
    { type: 'separator' },
    { type: 'item', text: 'Close', hotkey: 'Alt+F4' },
  ],
  Edit: [{ type: 'item', disable: true, text: 'Select All', hotkey: 'Ctrl+A' }],
  View: [{ type: 'item', disable: true, text: 'Refresh', hotkey: 'F5' }],
  Help: [{ type: 'item', disable: true, text: 'About Members' }],
};

const ITEMS = [
  {
    key: 'media',
    label: 'Media Room',
    icon: mediaPlayerIcon,
    tagline: 'Streaming video + audio for members. Coming soon.',
  },
  {
    key: 'chat',
    label: 'Members Chat',
    icon: chatIcon,
    tagline: 'Real-time chat. Coming soon.',
  },
  {
    key: 'games',
    label: 'Party Games',
    icon: pinballIcon,
    tagline: 'Multiplayer XP-style games. Coming soon.',
  },
  {
    key: 'share',
    label: 'Share Files',
    icon: shareIcon,
    tagline: 'Send files to other members. Coming soon.',
  },
];

function Members({ onClose }) {
  const { user } = useAuth();
  const [selectedKey, setSelectedKey] = useState(null);
  const [toast, setToast] = useState(null);

  const selected = ITEMS.find(it => it.key === selectedKey) || null;

  function comingSoon(item) {
    if (!item) return;
    setToast(item.label + ' — coming soon.');
    window.clearTimeout(comingSoon._t);
    comingSoon._t = window.setTimeout(() => setToast(null), 2200);
  }

  function onClickOptionItem(o) {
    if (o === 'Close') onClose();
    else if (o === 'Open') comingSoon(selected);
  }

  if (!user) {
    return (
      <Div>
        <div className="mem__empty">
          <img src={folderOpen} alt="" className="mem__empty__icon" />
          <p>Sign in to view members-only areas.</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Div>
    );
  }

  return (
    <Div>
      <section className="com__toolbar">
        <div className="com__options">
          <WindowDropDowns
            items={dropDownData}
            onClickItem={onClickOptionItem}
          />
        </div>
        <img className="com__windows-logo" src={windows} alt="windows" />
      </section>

      <section className="com__function_bar">
        <div className="com__function_bar__button--disable">
          <img className="com__function_bar__icon" src={back} alt="" />
          <span className="com__function_bar__text">Back</span>
          <div className="com__function_bar__arrow" />
        </div>
        <div className="com__function_bar__button--disable">
          <img className="com__function_bar__icon" src={forward} alt="" />
          <div className="com__function_bar__arrow" />
        </div>
        <div className="com__function_bar__button--disable">
          <img className="com__function_bar__icon--normalize" src={up} alt="" />
        </div>
        <div className="com__function_bar__separate" />
        <div className="com__function_bar__button--disable">
          <img
            className="com__function_bar__icon--normalize"
            src={search}
            alt=""
          />
          <span className="com__function_bar__text">Search</span>
        </div>
        <div className="com__function_bar__button--disable">
          <img
            className="com__function_bar__icon--normalize"
            src={folderOpen}
            alt=""
          />
          <span className="com__function_bar__text">Folders</span>
        </div>
        <div className="com__function_bar__separate" />
        <div className="com__function_bar__button">
          <img
            className="com__function_bar__icon--margin12"
            src={menu}
            alt=""
          />
          <div className="com__function_bar__arrow" />
        </div>
      </section>

      <section className="com__address_bar">
        <div className="com__address_bar__title">Address</div>
        <div className="com__address_bar__content">
          <img
            src={folderIcon}
            alt="folder"
            className="com__address_bar__content__img"
          />
          <div className="com__address_bar__content__text">C:\Members</div>
          <img
            src={dropdown}
            alt="dropdown"
            className="com__address_bar__content__img"
          />
        </div>
        <div className="com__address_bar__go">
          <img className="com__address_bar__go__img" src={go} alt="go" />
          <span className="com__address_bar__go__text">Go</span>
        </div>
      </section>

      <div className="com__content">
        <div className="com__content__inner">
          <div className="com__content__left">
            <div className="com__content__left__card">
              <div className="com__content__left__card__header">
                <div className="com__content__left__card__header__text">
                  Members Only
                </div>
                <img
                  src={pullup}
                  alt=""
                  className="com__content__left__card__header__img"
                />
              </div>
              <div className="com__content__left__card__content">
                <div className="mem__blurb">
                  Welcome, {user.display_name || user.username}. These features
                  are coming soon.
                </div>
              </div>
            </div>

            <div className="com__content__left__card">
              <div className="com__content__left__card__header">
                <div className="com__content__left__card__header__text">
                  Details
                </div>
                <img
                  src={pullup}
                  alt=""
                  className="com__content__left__card__header__img"
                />
              </div>
              <div className="com__content__left__card__content">
                {selected ? (
                  <>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text bold black">
                        {selected.label}
                      </div>
                    </div>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text black">
                        {selected.tagline}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="com__content__left__card__row">
                    <div className="com__content__left__card__text black">
                      Select an item to see details.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="com__content__right"
            onClick={e => {
              if (e.target === e.currentTarget) setSelectedKey(null);
            }}
          >
            <div className="com__content__right__card">
              <div className="com__content__right__card__header">
                Members Only Features
              </div>
              <div className="com__content__right__card__content">
                {ITEMS.map(item => (
                  <div
                    key={item.key}
                    className={
                      'com__content__right__card__item' +
                      (selectedKey === item.key ? ' mem__item--selected' : '')
                    }
                    onClick={() => setSelectedKey(item.key)}
                    onDoubleClick={() => comingSoon(item)}
                    title={item.label}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                      className="com__content__right__card__img"
                    />
                    <div className="com__content__right__card__img-container">
                      <div className="com__content__right__card__text">
                        {item.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {toast && <div className="mem__toast">{toast}</div>}
      </div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);

  .mem__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
    font-size: 12px;
    color: #333;
  }
  .mem__empty__icon {
    width: 48px;
    height: 48px;
    opacity: 0.7;
  }
  .mem__empty button {
    padding: 3px 12px;
  }

  .com__toolbar {
    position: relative;
    display: flex;
    align-items: center;
    line-height: 100%;
    height: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
  }
  .com__options {
    height: 23px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1px 0 1px 2px;
    border-left: 0;
    flex: 1;
  }
  .com__windows-logo {
    height: 100%;
    border-left: 1px solid white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .com__function_bar {
    height: 36px;
    display: flex;
    align-items: center;
    font-size: 11px;
    padding: 1px 3px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }
  .com__function_bar__button--disable {
    filter: grayscale(1);
    opacity: 0.7;
    display: flex;
    height: 100%;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0);
  }
  .com__function_bar__button {
    display: flex;
    height: 100%;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 3px;
    cursor: pointer;
  }
  .com__function_bar__text {
    margin-right: 4px;
  }
  .com__function_bar__icon {
    height: 30px;
    width: 30px;
    &--normalize {
      height: 22px;
      width: 22px;
      margin: 0 4px 0 1px;
    }
    &--margin12 {
      height: 22px;
      width: 22px;
      margin: 0 1px 0 2px;
    }
  }
  .com__function_bar__separate {
    height: 90%;
    width: 1px;
    background-color: rgba(0, 0, 0, 0.2);
    margin: 0 2px;
  }
  .com__function_bar__arrow {
    height: 100%;
    display: flex;
    align-items: center;
    margin: 0 4px;
    &:before {
      content: '';
      display: block;
      border-width: 3px 3px 0;
      border-color: #000 transparent;
      border-style: solid;
    }
  }

  .com__address_bar {
    flex-shrink: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.7);
    height: 20px;
    font-size: 11px;
    display: flex;
    align-items: center;
    padding: 0 2px;
    box-shadow: inset 0 -2px 3px -1px #b0b0b0;
    background: #fff;
  }
  .com__address_bar__title {
    line-height: 100%;
    color: rgba(0, 0, 0, 0.5);
    padding: 5px 5px 5px 3px;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
  }
  .com__address_bar__content {
    height: 100%;
    border: 1px solid rgba(122, 122, 255, 0.6);
    display: flex;
    align-items: center;
    flex: 1;
    margin-left: 3px;
    background: #fff;
    padding: 0 2px;
  }
  .com__address_bar__content__img {
    width: 14px;
    height: 14px;
  }
  .com__address_bar__content__text {
    flex: 1;
    font-size: 11px;
    color: #000;
    margin: 0 4px;
  }
  .com__address_bar__go {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 12px 0 5px;
  }
  .com__address_bar__go__img {
    width: 18px;
    height: 18px;
    margin-right: 3px;
  }

  .com__content {
    flex: 1;
    display: flex;
    overflow: hidden;
    position: relative;
  }
  .com__content__inner {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
  .com__content__left {
    width: 180px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: linear-gradient(to bottom, #7ba0e8 0%, #4c7ad2 100%);
    color: #fff;
    overflow-y: auto;
  }
  .com__content__left__card {
    background: rgba(255, 255, 255, 0.85);
    color: #000;
    border-radius: 3px;
    overflow: hidden;
  }
  .com__content__left__card__header {
    background: linear-gradient(to right, #f0f5ff 0%, #cfd8ea 100%);
    padding: 2px 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: bold;
    color: #123;
  }
  .com__content__left__card__header__img {
    width: 14px;
    height: 14px;
  }
  .com__content__left__card__content {
    padding: 5px 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 11px;
  }
  .com__content__left__card__row {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .com__content__left__card__text.black {
    color: #000;
  }
  .com__content__left__card__text.bold {
    font-weight: bold;
  }
  .mem__blurb {
    font-size: 11px;
    line-height: 1.4;
    color: #000;
  }

  .com__content__right {
    flex: 1;
    background: #fff;
    padding: 8px;
    overflow-y: auto;
  }
  .com__content__right__card {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .com__content__right__card__header {
    font-size: 11px;
    font-weight: bold;
    color: #345;
    border-bottom: 1px solid #d0d7e8;
    padding-bottom: 2px;
  }
  .com__content__right__card__content {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    padding: 6px 0;
  }
  .com__content__right__card__item {
    width: 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4px;
    border: 1px dotted transparent;
    cursor: default;
    text-align: center;
    user-select: none;
  }
  .com__content__right__card__item:hover {
    background: rgba(36, 94, 220, 0.08);
  }
  .com__content__right__card__img {
    width: 32px;
    height: 32px;
  }
  .com__content__right__card__img-container {
    margin-top: 3px;
    max-width: 100%;
  }
  .com__content__right__card__text {
    font-size: 11px;
    color: #000;
    overflow-wrap: anywhere;
    line-height: 1.2;
  }
  .mem__item--selected {
    background: rgba(36, 94, 220, 0.18) !important;
    border: 1px dotted #245edc !important;
  }
  .mem__item--selected .com__content__right__card__text {
    background: #316ac5;
    color: #fff;
    padding: 0 2px;
  }
  .mem__toast {
    position: absolute;
    bottom: 6px;
    right: 6px;
    background: #fff;
    border: 1px solid #b6b6b6;
    padding: 3px 8px;
    font-size: 11px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export default Members;
